import { useCallback, useState } from 'react';
import { cn } from '../utils/cn';

const COLLAPSE_PREFIX = 'bricklit-collapsed-sections:';

/**
 * 그룹 묶음 접힘 상태 — 목록(storageKey)마다 브라우저에 기억한다. 그룹이 많아지면
 * 필요 없는 묶음을 접어 두고 다음에 와도 그대로 보이게(2026-09-23).
 */
export function useCollapsedSections(storageKey: string): { isCollapsed: (key: string) => boolean; toggle: (key: string) => void } {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLLAPSE_PREFIX + storageKey) ?? '[]') as unknown;
      return new Set(Array.isArray(saved) ? saved.filter((key): key is string => typeof key === 'string') : []);
    } catch { return new Set(); }
  });
  const toggle = useCallback((key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(COLLAPSE_PREFIX + storageKey, JSON.stringify([...next])); } catch { /* 이번 화면에서만 유지 */ }
      return next;
    });
  }, [storageKey]);
  const isCollapsed = useCallback((key: string) => collapsed.has(key), [collapsed]);
  return { isCollapsed, toggle };
}

/** 그룹 묶음 머리 — '내 자료 / 그룹 이름'. 콘텐츠가 어느 그룹의 것인지 눈에 보이게(2026-09-15 A안).
 *  2026-09-16 대교 결정으로 `onAddFolder`(`+ 폴더`)는 없앴다 — 폴더 개념이 화면에서 통째로 빠졌다. */
export function GroupSectionLabel({ label, central, className, empty = false, collapsed, onToggle, count }: {
  label: string; central: boolean; className?: string;
  /** 콘텐츠가 하나도 없는 묶음 — 머리 아래에 한 줄 안내. */
  empty?: boolean;
  /** onToggle 을 주면 머리가 접기/펴기 버튼이 된다. 주지 않으면 예전처럼 제목만 그린다. */
  collapsed?: boolean;
  onToggle?: () => void;
  /** 접어도 몇 개가 들어 있는지 보이게 머리 끝에 적는다. */
  count?: number;
}): JSX.Element {
  // 접기 버튼이 있으면 불릿은 뺀다 — ▾와 ● 가 나란히 붙어 중복으로 보였다(2026-10-01 대교).
  // 대신 ▾를 불릿 크기만큼 키운다. 접기 없는 머리(예전 그대로)는 불릿이 유일한 표지라 남긴다.
  const title = <>
    {!onToggle && <span aria-hidden="true" className="h-1.5 w-1.5 flex-none rounded-full bg-text-dim" />}
    <span className="min-w-0 truncate">{label}</span>
    {count !== undefined && <span className="flex-none font-normal tabular-nums text-text-dim">{count}</span>}
  </>;
  return (
    <div className={cn('flex flex-col', className)}>
      {/* 글자 크기는 12px 하한, 회색은 한 단계 진하게(2026-09-16 검수 C2 — 10~11px·text-dimmer 남용).
          머리 자체는 한 줄로 고정한다: 좁아져도 낱자로 꺾이지 않게 label만 truncate(검수 C13). */}
      <div className="flex items-center gap-1.5 whitespace-nowrap px-1.5 pb-0.5 pt-1.5 text-[12px] font-semibold tracking-wide text-text-dim" role="heading" aria-level={3}>
        {onToggle ? (
          <button type="button" aria-expanded={!collapsed} onClick={onToggle}
            title={collapsed ? '펼치기' : '접기'}
            className="-mx-1 flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-panel-2 hover:text-text">
            <span aria-hidden="true" className={cn('flex-none w-3 text-center text-[14px] leading-none transition-transform duration-fast', collapsed ? '-rotate-90' : '')}>▾</span>
            {title}
          </button>
        ) : title}
      </div>
      {empty && !collapsed && <p className="px-1.5 pb-1 text-[12px] text-text-dim">콘텐츠가 없습니다.</p>}
    </div>
  );
}
